import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CadastroTrilhosNovosPage.css";
import { cadastrarFichaTrilhosNovos } from "../estaleiro.api";
import { validateCadastroTrilhosNovos } from "./rules/cadastroTrilhosNovosValidation";
import { listarPerfisTrilhoEstaleiro } from "../../bases-estaleiro/perfil-trilhos/perfilTrilhosEstaleiro.api";
import { listarOrigensCarregamentoTrilhosNovos } from "../../bases-estaleiro/origem-carregamento-trilhos-novos/origemCarregamentoTrilhosNovos.api";
import { listarVagoesEstaleiro } from "../../bases-estaleiro/vagoes/vagoesEstaleiro.api";
import { listarFabricantesEstaleiro } from "../../bases-estaleiro/fabricantes/fabricantesEstaleiro.api";
import { listarMotivosDefeitoEstaleiro } from "../../bases-estaleiro/motivo-defeito/motivoDefeitoEstaleiro.api";

const ROW_COUNT = 40;
const safeText = (value) => String(value ?? "").trim();
const isAtivo = (row) => Number(row?.ativo) === 1;
const toUniqueSorted = (items) =>
  Array.from(new Set(items.filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "pt-BR", { sensitivity: "base" })
  );

const formatOrigem = (row) => {
  const cidade = safeText(row?.cidade);
  const uf = safeText(row?.uf);
  if (!cidade && !uf) return "";
  if (!cidade) return uf;
  if (!uf) return cidade;
  return `${cidade} - ${uf}`;
};

const formatFabricante = (row) => {
  const joined = safeText(row?.fabricante_modelo);
  if (joined) return joined;
  const fabricante = safeText(row?.fabricante);
  const modelo = safeText(row?.modelo);
  if (!fabricante) return "";
  return modelo ? `${fabricante} - ${modelo}` : fabricante;
};

const formatVagao = (row) => {
  const codigoVagao = safeText(row?.codigo_vagao);
  if (codigoVagao) return codigoVagao;
  const completo = safeText(row?.codigo_vagao_completo);
  if (completo) return completo;
  const serie = safeText(row?.serie_vagao);
  const codigo = safeText(row?.numero_vagao);
  if (!serie && !codigo) return "";
  if (!serie) return codigo;
  if (!codigo) return serie;
  return `${serie} ${codigo}`;
};

const todayYMD = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const createRows = () =>
  Array.from({ length: ROW_COUNT }, (_, index) => ({
    id: index + 1,
    vagao1: "",
    vagao2: "",
    fabricante: "",
    quantidadePecas: "",
    metragemPecas: "",
    furos: "",
    qtdFuros: "",
    defeito: "",
    qtdDefeito: "",
    motivoDefeito: "",
  }));

const INITIAL_FORM = {
  data: todayYMD(),
  perfilTrilho: "",
  origem: "",
  pedidoTransferencia: "",
};

const toNumber = (value) => {
  const normalized = String(value || "").replace(",", ".").trim();
  if (!normalized) return 0;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const joinVagoes = (row) => {
  const v1 = String(row?.vagao1 || "").trim();
  const v2 = String(row?.vagao2 || "").trim();
  if (!v1) return "";
  return v2 ? `${v1} | ${v2}` : v1;
};

function SmoothSelect({ value, onChange, options, placeholder = "Selecione", className = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const prevValueRef = useRef(value);

  const closeDropdown = () => {
    setOpen(false);
    window.requestAnimationFrame(() => {
      triggerRef.current?.blur?.();
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    const prev = String(prevValueRef.current ?? "");
    const next = String(value ?? "");
    if (open && prev !== next) setOpen(false);
    prevValueRef.current = value;
  }, [value, open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div ref={rootRef} className={`ctn-pick ${open ? "is-open" : ""} ${className}`.trim()}>
      <button
        type="button"
        className="ctn-pick-btn"
        ref={triggerRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
      >
        <span className={`ctn-pick-label ${selected ? "" : "is-placeholder"}`}>{selected?.label || placeholder}</span>
      </button>
      {open ? (
        <div className="ctn-pick-menu" role="listbox">
          {options.map((option) => (
            <button
              type="button"
              key={`${option.value}-${option.label}`}
              className={`ctn-pick-opt ${value === option.value ? "is-selected" : ""}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange(option.value);
                closeDropdown();
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SearchableSelect({ value, onChange, options, placeholder = "Digite para buscar", className = "" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const prevValueRef = useRef(value);

  const closeDropdown = () => {
    setOpen(false);
    window.requestAnimationFrame(() => {
      inputRef.current?.blur?.();
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    const prev = String(prevValueRef.current ?? "");
    const next = String(value ?? "");
    if (open && prev !== next) closeDropdown();
    prevValueRef.current = value;
  }, [value, open]);

  const filteredOptions = useMemo(() => {
    const query = String(value || "").toUpperCase().trim();
    if (!query) return options;
    return options.filter((option) => String(option).toUpperCase().includes(query));
  }, [options, value]);

  return (
    <div ref={rootRef} className={`ctn-combo ${open ? "is-open" : ""} ${className}`.trim()}>
      <div className="ctn-combo-box">
        <input
          type="text"
          className="ctn-combo-input"
          ref={inputRef}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="ctn-combo-toggle"
          onClick={() => setOpen((prev) => !prev)}
          aria-label="Abrir lista de códigos de vagão"
          aria-haspopup="listbox"
          aria-expanded={open}
        />
      </div>

      {open ? (
        <div className="ctn-combo-menu" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                type="button"
                key={option}
                className={`ctn-combo-opt ${value === option ? "is-selected" : ""}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(option);
                  closeDropdown();
                }}
              >
                {option}
              </button>
            ))
          ) : (
            <p className="ctn-combo-empty">Nenhum código encontrado</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function CadastroTrilhosNovosPage({ onBack }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [rows, setRows] = useState(() => createRows());
  const [perfilOptions, setPerfilOptions] = useState([]);
  const [origemOptions, setOrigemOptions] = useState([]);
  const [vagaoOptions, setVagaoOptions] = useState([]);
  const [fabricanteOptions, setFabricanteOptions] = useState([]);
  const [motivoDefeitoOptions, setMotivoDefeitoOptions] = useState([]);
  const [basesLoading, setBasesLoading] = useState(true);
  const [basesError, setBasesError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationModalGroups, setValidationModalGroups] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const carregarBases = async () => {
      setBasesLoading(true);
      setBasesError("");

      try {
        const [perfis, origens, vagoes, fabricantes, motivosDefeito] = await Promise.all([
          listarPerfisTrilhoEstaleiro({ limit: 1000, offset: 0 }),
          listarOrigensCarregamentoTrilhosNovos({ limit: 1000, offset: 0 }),
          listarVagoesEstaleiro({ limit: 1000, offset: 0 }),
          listarFabricantesEstaleiro({ limit: 1000, offset: 0 }),
          listarMotivosDefeitoEstaleiro({ limit: 1000, offset: 0 }),
        ]);

        if (!isMounted) return;

        setPerfilOptions(toUniqueSorted(perfis.filter(isAtivo).map((row) => safeText(row?.perfil_trilho))));
        setOrigemOptions(toUniqueSorted(origens.filter(isAtivo).map(formatOrigem)));
        setVagaoOptions(toUniqueSorted(vagoes.filter(isAtivo).map(formatVagao)));
        setFabricanteOptions(toUniqueSorted(fabricantes.filter(isAtivo).map(formatFabricante)));
        setMotivoDefeitoOptions(toUniqueSorted(motivosDefeito.filter(isAtivo).map((row) => safeText(row?.motivo_defeito))));
      } catch (error) {
        if (!isMounted) return;

        setPerfilOptions([]);
        setOrigemOptions([]);
        setVagaoOptions([]);
        setFabricanteOptions([]);
        setMotivoDefeitoOptions([]);
        setBasesError(`Não foi possível carregar as bases do cadastro (${String(error?.message || "erro desconhecido").trim()}).`);
      } finally {
        if (isMounted) setBasesLoading(false);
      }
    };

    carregarBases();
    return () => {
      isMounted = false;
    };
  }, []);

  const perfilSelectOptions = useMemo(
    () => perfilOptions.map((option) => ({ value: option, label: option })),
    [perfilOptions]
  );

  const origemSelectOptions = useMemo(
    () => origemOptions.map((option) => ({ value: option, label: option })),
    [origemOptions]
  );

  const fabricanteSelectOptions = useMemo(
    () => [{ value: "", label: "Selecione" }, ...fabricanteOptions.map((option) => ({ value: option, label: option }))],
    [fabricanteOptions]
  );

  const motivoDefeitoSelectOptions = useMemo(
    () => [{ value: "", label: "Selecione" }, ...motivoDefeitoOptions.map((option) => ({ value: option, label: option }))],
    [motivoDefeitoOptions]
  );

  const totais = useMemo(() => {
    const totalPecas = rows.reduce((acc, row) => acc + toNumber(row.quantidadePecas), 0);
    const totalMetragem = rows.reduce((acc, row) => {
      const quantidade = toNumber(row.quantidadePecas);
      const metragemPorPeca = toNumber(row.metragemPecas);
      return acc + quantidade * metragemPorPeca;
    }, 0);
    return { totalPecas, totalMetragem };
  }, [rows]);

  const onChangeField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onChangeRow = (rowIndex, field, value) => {
    setRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) return row;
        if (field === "furos") {
          return {
            ...row,
            furos: value,
            qtdFuros: value === "Sim" ? row.qtdFuros : "",
          };
        }
        if (field === "defeito") {
          return {
            ...row,
            defeito: value,
            qtdDefeito: value === "Sim" ? row.qtdDefeito : "",
            motivoDefeito: value === "Sim" ? row.motivoDefeito : "",
          };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const resetFicha = () => {
    setForm({ ...INITIAL_FORM, data: todayYMD() });
    setRows(createRows());
    setValidationModalGroups([]);
  };

  const onClear = () => {
    resetFicha();
    setSaveMessage("");
    setSaveError(false);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSaveMessage("");
    setSaveError(false);
    setValidationModalGroups([]);

    const validation = validateCadastroTrilhosNovos({ form, rows });
    if (!validation.isValid) {
      setSaveMessage(validation.firstError);
      setSaveError(true);
      setValidationModalGroups(validation.modalGroups);
      setIsSaving(false);
      return;
    }

    const rowsPayload = rows.map((row) => ({
      ...row,
      vagao: joinVagoes(row),
    }));

    try {
      const data = await cadastrarFichaTrilhosNovos({ form, rows: rowsPayload });
      const lote = data?.codigo_lote ? ` Lote: ${data.codigo_lote}.` : "";
      resetFicha();
      setSaveMessage(`Ficha salva no servidor.${lote}`);
      setSaveError(false);
    } catch (error) {
      const detail = String(error?.message || "erro ao salvar").trim();
      setSaveMessage(`Não foi possível salvar no servidor (${detail}).`);
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const onCloseValidationModal = () => {
    setValidationModalGroups([]);
  };

  return (
    <main className="ctn-page">
      <div className="ctn-root">
        <aside className="ctn-sidebar">
          <button type="button" className="ctn-back" onClick={onBack}>
            Menu Principal
          </button>
          <p>Painel de apoio para lançamento em campo.</p>

          <section className="ctn-side-card">
            <h3>Dados da Ficha</h3>
            <label className="ctn-field">
              <span>Data</span>
              <input type="date" name="data" value={form.data} onChange={onChangeField} required />
            </label>

            <div className="ctn-field">
              <span>Perfil de trilho</span>
              <SmoothSelect
                value={form.perfilTrilho}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, perfilTrilho: nextValue }))}
                options={perfilSelectOptions}
                className="ctn-pick--sidebar"
                disabled={basesLoading}
              />
            </div>

            <div className="ctn-field">
              <span>Origem</span>
              <SmoothSelect
                value={form.origem}
                onChange={(nextValue) => setForm((prev) => ({ ...prev, origem: nextValue }))}
                options={origemSelectOptions}
                className="ctn-pick--sidebar"
                disabled={basesLoading}
              />
            </div>

            <label className="ctn-field">
              <span>Pedido de transferência</span>
              <input
                type="text"
                name="pedidoTransferencia"
                value={form.pedidoTransferencia}
                onChange={onChangeField}
                placeholder="Ex: 330301"
              />
            </label>
          </section>

          {basesError ? <p className="ctn-saved is-error">{basesError}</p> : null}

          <section className="ctn-side-card">
            <h3>Passos</h3>
            <div className="ctn-sidebar-steps">
              <p>1. Preencha os dados gerais.</p>
              <p>2. Lance os vagões na tabela.</p>
              <p>3. Salve a ficha para enviar ao servidor.</p>
            </div>
          </section>
        </aside>

        <section className="ctn-content">
          <header className="ctn-header">
            <div className="ctn-header-copy">
              <h1>Cadastro de Trilhos Novos</h1>
              <p className="ctn-subtitle">Controle de entrada de trilhos novos.</p>
            </div>
          </header>

          <form id="ctn-form" className="ctn-card" onSubmit={onSubmit}>
            <section className="ctn-table-wrap">
              <div className="ctn-table-shell">
                <table className="ctn-table ctn-table--head">
                  <colgroup>
                    <col className="ctn-w-id" />
                    <col className="ctn-w-vagao" />
                    <col className="ctn-w-fabricante" />
                    <col className="ctn-w-qtd" />
                    <col className="ctn-w-metros" />
                    <col className="ctn-w-furos" />
                    <col className="ctn-w-qtd-furos" />
                    <col className="ctn-w-defeito" />
                    <col className="ctn-w-qtd-defeito" />
                    <col className="ctn-w-motivo-defeito" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="ctn-col-id">N</th>
                      <th className="ctn-col-vagao">Código dos vagões</th>
                      <th className="ctn-col-fabricante">Fabricante</th>
                      <th className="ctn-col-qtd">Qtd. peças</th>
                      <th className="ctn-col-metros">Metragem</th>
                      <th className="ctn-col-furos">Furos</th>
                      <th className="ctn-col-qtd-furos">Qtd. com furos</th>
                      <th className="ctn-col-defeito">Defeito</th>
                      <th className="ctn-col-qtd-defeito">Qtd. com defeito</th>
                      <th className="ctn-col-motivo-defeito">Motivo do defeito</th>
                    </tr>
                  </thead>
                </table>

                <div className="ctn-table-body-wrap">
                  <table className="ctn-table ctn-table--body">
                    <colgroup>
                      <col className="ctn-w-id" />
                      <col className="ctn-w-vagao" />
                      <col className="ctn-w-fabricante" />
                      <col className="ctn-w-qtd" />
                      <col className="ctn-w-metros" />
                      <col className="ctn-w-furos" />
                      <col className="ctn-w-qtd-furos" />
                      <col className="ctn-w-defeito" />
                      <col className="ctn-w-qtd-defeito" />
                      <col className="ctn-w-motivo-defeito" />
                    </colgroup>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="ctn-row-id">{row.id}</td>
                          <td>
                            <div className="ctn-vagoes-pair">
                              <SearchableSelect
                                value={row.vagao1}
                                onChange={(nextValue) => onChangeRow(index, "vagao1", nextValue)}
                                options={vagaoOptions}
                                placeholder="Vagão 1 *"
                                className="ctn-combo--cell"
                              />
                              <SearchableSelect
                                value={row.vagao2}
                                onChange={(nextValue) => onChangeRow(index, "vagao2", nextValue)}
                                options={vagaoOptions}
                                placeholder="Vagão 2"
                                className="ctn-combo--cell"
                              />
                            </div>
                          </td>
                          <td>
                            <SmoothSelect
                              value={row.fabricante}
                              onChange={(nextValue) => onChangeRow(index, "fabricante", nextValue)}
                              placeholder="Selecione"
                              className="ctn-pick--cell ctn-pick--light"
                              options={fabricanteSelectOptions}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.quantidadePecas}
                              inputMode="numeric"
                              onChange={(event) => onChangeRow(index, "quantidadePecas", event.target.value)}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.metragemPecas}
                              inputMode="decimal"
                              onChange={(event) => onChangeRow(index, "metragemPecas", event.target.value)}
                              placeholder="0,00"
                            />
                          </td>
                          <td>
                            <SmoothSelect
                              value={row.furos}
                              onChange={(nextValue) => onChangeRow(index, "furos", nextValue)}
                              placeholder="Selecione"
                              className="ctn-pick--cell ctn-pick--light"
                              options={[
                                { value: "", label: "Selecione" },
                                { value: "Sim", label: "Sim" },
                                { value: "Não", label: "Não" },
                              ]}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.qtdFuros}
                              inputMode="numeric"
                              onChange={(event) => onChangeRow(index, "qtdFuros", event.target.value)}
                              placeholder="0"
                              disabled={row.furos !== "Sim"}
                            />
                          </td>
                          <td>
                            <SmoothSelect
                              value={row.defeito}
                              onChange={(nextValue) => onChangeRow(index, "defeito", nextValue)}
                              placeholder="Selecione"
                              className="ctn-pick--cell ctn-pick--light"
                              options={[
                                { value: "", label: "Selecione" },
                                { value: "Sim", label: "Sim" },
                                { value: "Não", label: "Não" },
                              ]}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={row.qtdDefeito}
                              inputMode="numeric"
                              onChange={(event) => onChangeRow(index, "qtdDefeito", event.target.value)}
                              placeholder="0"
                              disabled={row.defeito !== "Sim"}
                            />
                          </td>
                          <td>
                            <SmoothSelect
                              value={row.motivoDefeito}
                              onChange={(nextValue) => onChangeRow(index, "motivoDefeito", nextValue)}
                              placeholder="Selecione"
                              className="ctn-pick--cell ctn-pick--light"
                              disabled={row.defeito !== "Sim"}
                              options={motivoDefeitoSelectOptions}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {saveMessage ? <p className={`ctn-saved ${saveError ? "is-error" : ""}`}>{saveMessage}</p> : null}
          </form>

          <footer className="ctn-actions">
            <div className="ctn-actions-totals">
              <p>
                Total de peças: <strong>{totais.totalPecas}</strong>
              </p>
              <p>
                Total de metragem: <strong>{totais.totalMetragem.toFixed(2)}</strong>
              </p>
            </div>
            <div className="ctn-actions-buttons">
              <button type="button" className="ctn-btn ctn-btn--ghost" onClick={onClear}>
                Limpar
              </button>
              <button type="submit" className="ctn-btn ctn-btn--primary" form="ctn-form" disabled={isSaving}>
                {isSaving ? "Salvando..." : "Salvar ficha"}
              </button>
            </div>
          </footer>

          {validationModalGroups.length ? (
            <div
              className="ctn-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ctn-modal-title"
              onClick={onCloseValidationModal}
            >
              <div className="ctn-modal-card" onClick={(event) => event.stopPropagation()}>
                <div className="ctn-modal-head">
                  <div className="ctn-modal-alert">!</div>
                  <div className="ctn-modal-title-wrap">
                    <h3 id="ctn-modal-title">Pendências para salvar</h3>
                    <p>Corrija os itens abaixo antes de salvar a ficha.</p>
                  </div>
                  <button type="button" className="ctn-modal-close" onClick={onCloseValidationModal} aria-label="Fechar">
                    x
                  </button>
                </div>

                <p className="ctn-modal-count">
                  {validationModalGroups.reduce((acc, group) => acc + group.errors.length, 0)} pendência(s)
                  encontrada(s)
                </p>
                <div className="ctn-modal-groups">
                  {validationModalGroups.map((group) => (
                    <section key={group.id} className="ctn-modal-group">
                      <h4>{group.title}</h4>
                      <ul className="ctn-modal-list">
                        {group.errors.map((item, index) => (
                          <li key={`${group.id}-${index}-${item}`}>{item}</li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
                <div className="ctn-modal-actions">
                  <button type="button" className="ctn-btn ctn-btn--primary" onClick={onCloseValidationModal}>
                    Entendi
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}



